import React, { useState, useRef, useEffect } from "react";
import { 
  FiCalendar, 
  FiRefreshCw, 
  FiGrid, 
  FiActivity, 
  FiMic, 
  FiBarChart2 
} from "react-icons/fi";
import { BiChevronDown } from "react-icons/bi";
import FadeIn from "react-fade-in";
import { motion, AnimatePresence } from "framer-motion";
import ThemedButton from "../../../atom/ThemedButton/themedButton";
import TranscriptionOverview from "./transcriptionOverview";
import TranscriptionSTT from "./transcriptionSTT";
import TranscriptionAnalytics from "./transcriptionAnalytics";
import TranscriptionProcessing from "./transcriptionProcessing";

// ─── Helpers ────────────────────────────────────────────────────────────────
const toISO = (d: Date) => d.toISOString().split("T")[0];

const getRangeForPreset = (preset: string): { start: string; end: string } => {
  const now = new Date();
  const today = toISO(now);
  switch (preset) {
    case "today":
      return { start: today, end: today };
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yd = toISO(y);
      return { start: yd, end: yd };
    }
    case "last_week": {
      const w = new Date(now);
      w.setDate(w.getDate() - 7);
      return { start: toISO(w), end: today };
    }
    case "last_30": {
      const m = new Date(now);
      m.setDate(m.getDate() - 30);
      return { start: toISO(m), end: today };
    }
    default:
      return { start: today, end: today };
  }
};

const PRESET_LABELS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last_week: "Last Week",
  last_30: "Last 30 Days",
  custom: "Custom",
};

const TABS = [
  { id: "overview", label: "Overview", icon: <FiGrid size={16} /> },
  { id: "stt", label: "Speech to Text", icon: <FiMic size={16} /> },
  { id: "analytics", label: "LLM Analytics", icon: <FiBarChart2 size={16} /> },
  { id: "processing", label: "Processing", icon: <FiActivity size={16} /> },

];

const Transcription = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const initialRange = getRangeForPreset("today");
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [customFrom, setCustomFrom] = useState(initialRange.start);
  const [customTo, setCustomTo] = useState(initialRange.end);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const todayStr = toISO(new Date());

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handlePresetSelect = (preset: string) => {
    setSelectedPreset(preset);
    setDropdownOpen(false);
    if (preset !== "custom") {
      const range = getRangeForPreset(preset);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  const applyCustomRange = () => {
    setStartDate(customFrom);
    setEndDate(customTo);
  };

  return (
    <div className="py-3 overflow-auto h-full w-full">
      <div className="flex flex-col h-[calc(100vh-125px)] w-full p-8 bg-[#f8fafc] overflow-auto rounded-md">
        {/* ── Header ── */}
        <div className="flex justify-between items-center mb-10 -mt-4 border-b border-[#e5dddd92] pb-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Transcription Insight
            </h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Real-time transcription performance and analytics engine</p>
          </div>
          <ThemedButton
            children={"Refresh Data"}
            theme={"primary"}
            click={() => setRefreshTrigger((prev) => prev + 1)}
          />
        </div>

        {/* ── Tabs + Date-range selector ── */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
          {/* Enhanced Tabs - Responsive Container */}
          <div className="relative flex items-center p-1 bg-slate-100/80 backdrop-blur-sm rounded-[18px] border border-slate-200/60 shadow-sm overflow-x-auto overflow-y-hidden no-scrollbar max-w-full w-fit">
            <div className="flex flex-nowrap min-w-max">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      relative px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold transition-all duration-300 flex items-center gap-2 z-10 whitespace-nowrap
                      ${isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-700"}
                    `}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-white rounded-[14px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-slate-200/50"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className={`relative z-20 ${isActive ? "text-[#feba12]" : "text-slate-400"}`}>
                      {tab.icon}
                    </span>
                    <span className="relative z-20">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date-range controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {/* Preset dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((p) => !p)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-[#ccc] rounded-xl shadow-sm transition-colors min-w-[150px] bg-white"
              >
                <FiCalendar size={14} className="text-[#feba12]" />
                <span className="flex-1 text-left text-gray-700">
                  {PRESET_LABELS[selectedPreset]}
                </span>
                <BiChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-1 w-44 border border-[#ccc] rounded-xl shadow-lg z-50 overflow-hidden bg-[#fff]">
                  {Object.entries(PRESET_LABELS).map(([key, label]) => (
                    <FadeIn key={key}>
                      <button
                        onClick={() => handlePresetSelect(key)}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          selectedPreset === key
                            ? "bg-[#fef4e9] text-[#feba12] font-semibold"
                            : "hover:bg-gray-50 text-gray-600"
                        }`}
                      >
                        {label}
                      </button>
                    </FadeIn>
                  ))}
                </div>
              )}
            </div>

            {/* Custom date picker */}
            {selectedPreset === "custom" && (
              <div className="flex items-center gap-2 bg-white border border-[#ccc] rounded-xl px-3 py-2 shadow-sm">
                <span className="text-xs text-gray-500 font-medium">From</span>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || todayStr}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="text-sm text-gray-700 border-none outline-none bg-transparent cursor-pointer"
                />
                <span className="text-gray-300 select-none">|</span>
                <span className="text-xs text-gray-500 font-medium">To</span>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  max={todayStr}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="text-sm text-gray-700 border-none outline-none bg-transparent cursor-pointer"
                />
                <button
                  onClick={applyCustomRange}
                  className="ml-1 px-3 py-1 text-xs font-semibold bg-[#feba12] text-white rounded-md hover:bg-[#e5a80e] transition-colors"
                >
                  Apply
                </button>
              </div>
            )}

            {/* Resolved date range label */}
            {selectedPreset !== "custom" && (
              <span className="text-xs text-[#64748b]">
                {startDate === endDate ? startDate : `${startDate} → ${endDate}`}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        {activeTab === "overview" && (
          <TranscriptionOverview 
            startDate={startDate} 
            endDate={endDate} 
            refreshTrigger={refreshTrigger} 
          />
        )}
        {activeTab === "processing" && (
          <TranscriptionProcessing 
            startDate={startDate} 
            endDate={endDate} 
            refreshTrigger={refreshTrigger} 
          />
        )}
        {activeTab === "stt" && (
          <TranscriptionSTT 
            startDate={startDate} 
            endDate={endDate} 
            refreshTrigger={refreshTrigger} 
          />
        )}
        {activeTab === "analytics" && (
          <TranscriptionAnalytics 
            startDate={startDate} 
            endDate={endDate} 
            refreshTrigger={refreshTrigger} 
          />
        )}
      </div>
    </div>
  );
}

export default Transcription;
